import { debounce, DebouncedFunc } from 'lodash';
import React from 'react';
import Contact from '../../models/Contact';
import InvestmentEntityContact, {
  ContactRole,
  RepresentativeType,
} from '../../models/InvestmentEntityContact';
import {
  ContactAssociationActionButtons,
  ContactAssociationAssociatedContacts,
  ContactAssociationLoadingCard,
  ContactAssociationRoleSelectionSection,
  ContactAssociationSearchSection,
  ContactAssociationSharedHeader,
} from './ContactAssociationStepSections';
import {
  ContactAssociationState,
  ContactAssociationStepProps,
  createInitialContactAssociationState,
  EMPTY_ADD_CONTACT_MODAL,
} from './contactAssociationTypes';
import {
  canCompleteWithoutAssociatedContacts,
  getAvailableContactRoles,
  getContactRoleDescription,
  getContactValidationErrors,
} from './contactAssociationUtils';
import { StyledCard, StyledCardContent } from '../styles/styles';

export type {
  ContactAssociationState,
  ContactAssociationStepProps,
} from './contactAssociationTypes';

export abstract class ContactAssociationStepBase<
  TProps extends ContactAssociationStepProps = ContactAssociationStepProps,
> extends React.Component<TProps, ContactAssociationState> {
  protected searchInputRef = React.createRef<HTMLInputElement>();
  protected otherRepresentativeTypeRef = React.createRef<HTMLInputElement>();
  protected debouncedSearch: DebouncedFunc<(query: string) => void>;

  constructor(props: TProps) {
    super(props);
    this.state = createInitialContactAssociationState();
    this.debouncedSearch = debounce(this.performSearch.bind(this), 300);
  }

  componentDidMount() {
    this.loadAssociatedContacts();
  }

  componentDidUpdate(prevProps: TProps, prevState: ContactAssociationState) {
    if (
      !this.state.addContactModal.contact &&
      !this.state.loading &&
      (prevState.addContactModal.contact || prevState.loading)
    ) {
      requestAnimationFrame(() => {
        this.searchInputRef.current?.focus();
      });
    }

    if (
      this.state.addContactModal.representativeType === 'OTHER' &&
      prevState.addContactModal.representativeType !== 'OTHER'
    ) {
      setTimeout(() => {
        this.otherRepresentativeTypeRef.current?.focus();
      }, 100);
    }

    if (prevState.associatedContacts !== this.state.associatedContacts) {
      this.validateContactRequirements();
    }
  }

  protected abstract performSearch(query: string): void;
  protected abstract getBackendService(): any;
  protected abstract renderContactCard(contact: Contact, isSearchResult: boolean): React.ReactNode;
  protected abstract renderAssociatedContactCard(contact: InvestmentEntityContact): React.ReactNode;

  protected async retrieveContacts() {
    return await this.getBackendService().getEntityContacts(this.props.entityId);
  }

  protected async loadAssociatedContacts() {
    try {
      this.setState({ loading: true, error: null });
      const contacts = await this.retrieveContacts();

      this.setState({
        associatedContacts: contacts,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading associated contacts:', error);
      this.setState({
        error: 'Failed to load associated contacts',
        loading: false,
      });
    }
  }

  protected validateContactRequirements() {
    this.setState({
      validationErrors: getContactValidationErrors(this.state.associatedContacts),
    });
  }

  protected handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    this.setState({ searchQuery: query });
    this.debouncedSearch(query);
  };

  protected handleAddContact = async (contact: Contact) => {
    this.setState({
      addContactModal: {
        open: false,
        contact,
        role: 'OWNER',
      },
    });
  };

  protected handleCloseAddContactModal = () => {
    this.setState({
      searchQuery: '',
      searchResults: [],
      addContactModal: { ...EMPTY_ADD_CONTACT_MODAL },
    });

    setTimeout(() => {
      this.searchInputRef.current?.focus();
    }, 100);
  };

  protected handleRoleChange = (role: ContactRole) => {
    this.setState({
      addContactModal: {
        ...this.state.addContactModal,
        role,
      },
    });
  };

  protected handleRepresentativeTypeChange = (representativeType: RepresentativeType) => {
    this.setState({
      addContactModal: {
        ...this.state.addContactModal,
        representativeType,
      },
    });
  };

  protected handleOtherRepresentativeTypeChange = (otherRepresentativeType: string) => {
    this.setState({
      addContactModal: {
        ...this.state.addContactModal,
        otherRepresentativeType,
      },
    });
  };

  protected handleConfirmAddContact = async () => {
    const { contact, role, representativeType, otherRepresentativeType } =
      this.state.addContactModal;
    if (!contact) return;

    try {
      this.setState({ associating: true, error: null });
      const backendService = this.getBackendService();
      await backendService.addContactToEntity(
        this.props.entityId,
        contact.id,
        role,
        representativeType,
        representativeType === 'OTHER' ? otherRepresentativeType : undefined,
      );

      await this.loadAssociatedContacts();

      this.setState({
        searchQuery: '',
        searchResults: [],
        associating: false,
        addContactModal: { ...EMPTY_ADD_CONTACT_MODAL },
      });
    } catch (error) {
      console.error('Error adding contact:', error);
      this.setState({
        error: `Failed to add contact: ${error}`,
        associating: false,
      });
    }
  };

  protected handleDeleteContact = async (contactId: string) => {
    try {
      this.setState({ associating: true, error: null });
      const backendService = this.getBackendService();
      await backendService.removeContactFromEntity(this.props.entityId, contactId);
      await this.loadAssociatedContacts();
      this.setState({ associating: false });
    } catch (error) {
      console.error('Error removing contact:', error);
      this.setState({
        error: `Failed to remove contact: ${error}`,
        associating: false,
      });
    }
  };

  protected getAvailableRoles = (): ContactRole[] => {
    return getAvailableContactRoles(this.props.entityType, this.state.associatedContacts);
  };

  protected getRoleDescription = (role: ContactRole): string => {
    return getContactRoleDescription(this.props.entityType, role);
  };

  protected canCompleteWithoutContacts = (): boolean => {
    return canCompleteWithoutAssociatedContacts(this.props.entityType);
  };

  protected renderSharedHeader() {
    return (
      <ContactAssociationSharedHeader
        entityName={this.props.entityName}
        entityType={this.props.entityType}
        error={this.state.error}
        validationErrors={this.state.validationErrors}
      />
    );
  }

  protected renderAssociatedContacts() {
    return (
      <ContactAssociationAssociatedContacts
        associatedContacts={this.state.associatedContacts}
        renderAssociatedContactCard={(contact) => this.renderAssociatedContactCard(contact)}
      />
    );
  }

  protected renderContactSearchSection() {
    return (
      <ContactAssociationSearchSection
        searchInputRef={this.searchInputRef}
        searchQuery={this.state.searchQuery}
        searchLoading={this.state.searchLoading}
        searchResults={this.state.searchResults}
        addContactSelected={!!this.state.addContactModal.contact}
        onSearchChange={this.handleSearchChange}
        renderContactCard={(contact, isSearchResult) =>
          this.renderContactCard(contact, isSearchResult)
        }
      />
    );
  }

  protected renderRoleSelectionSection() {
    return (
      <ContactAssociationRoleSelectionSection
        entityName={this.props.entityName}
        addContactModal={this.state.addContactModal}
        associating={this.state.associating}
        availableRoles={this.getAvailableRoles()}
        otherRepresentativeTypeRef={this.otherRepresentativeTypeRef}
        onRoleChange={this.handleRoleChange}
        onRepresentativeTypeChange={this.handleRepresentativeTypeChange}
        onOtherRepresentativeTypeChange={this.handleOtherRepresentativeTypeChange}
        onConfirmAddContact={this.handleConfirmAddContact}
        onCancelAddContact={this.handleCloseAddContactModal}
      />
    );
  }

  protected renderActionButtons() {
    return (
      <ContactAssociationActionButtons
        addContactSelected={!!this.state.addContactModal.contact}
        associatedContactCount={this.state.associatedContacts.length}
        associating={this.state.associating}
        validationErrors={this.state.validationErrors}
        canCompleteWithoutContacts={this.canCompleteWithoutContacts()}
        onComplete={this.props.onComplete}
        onSkip={this.props.onSkip}
      />
    );
  }

  render() {
    if (this.state.loading) {
      return <ContactAssociationLoadingCard />;
    }

    return (
      <StyledCard tone="tone1">
        <StyledCardContent sx={{ py: 3 }}>
          {this.renderSharedHeader()}
          {this.renderAssociatedContacts()}
          {this.renderContactSearchSection()}
          {this.renderRoleSelectionSection()}
          {this.renderActionButtons()}
        </StyledCardContent>
      </StyledCard>
    );
  }
}
